// INVDESIGN1-S2 (docs/build-briefs/INVDESIGN1-S2-home-setup-review-order.md; owner-approved mockup invdesign1-5). The review
// before closing a count shows, per item and in total, how far the count is from what was expected, in quantity, percent
// and dollars. This module is the pure arithmetic and the one named threshold. No I/O, no DOM, so the server, the screen
// and the tests read the very same rule.
//
// Dollars are never computed here. They come from the stock-value valuation (createItemValuer in stockValueReport.ts: the
// same cost basis and the same single half-up rounding the Stock value figure uses). This file only subtracts them.

/** The designer's threshold (mockup 5): an item whose count is off from expected by this percent or more is marked Check and
 * sorts first. One named constant; change it here and the screen, the sort and the tests follow. */
export const COUNT_CHECK_PERCENT = 10;

/** Percent off to one decimal, the figure the row shows, or null when there is no expectation to measure against (expected is
 * zero or less, as on a first count). Positive is over, negative is short. */
export function percentOff(expected: number, counted: number): number | null {
  if (!Number.isFinite(expected) || !Number.isFinite(counted) || expected <= 0) return null;
  const raw = ((counted - expected) / expected) * 100;
  const rounded = Math.round(raw * 10) / 10;
  return Object.is(rounded, -0) ? 0 : rounded;
}

/** True when the row needs a second look. Judged on the percent as shown (one decimal), so a row that reads "-10.0%" is
 * always marked and a row that reads "-9.9%" never is. A row with no percent (nothing expected) is not marked: a first count
 * has nothing to be off from. */
export function needsCheck(percent: number | null): boolean {
  return percent !== null && Math.abs(percent) >= COUNT_CHECK_PERCENT;
}

export interface DollarVariance {
  readonly expectedCents: number;
  readonly countedCents: number;
  readonly differenceCents: number;
  /** Difference as a percent of expected dollars, one decimal; null when expected is zero or less. */
  readonly percent: number | null;
}

/** The tiles' arithmetic: whole cents in, whole cents out. */
export function dollarVariance(expectedCents: number, countedCents: number): DollarVariance {
  return {
    expectedCents,
    countedCents,
    differenceCents: countedCents - expectedCents,
    percent: percentOff(expectedCents, countedCents),
  };
}
